import { CurrencyDollarIcon } from "@heroicons/react/24/outline";
import { useVerification } from "src/contexts/VerificationContext";
import { useEffect, useState, useCallback, useMemo } from "react";
import BlurredCard from "../BlurredCard";
import Loading from "../Loading";
import { apiGet } from "src/utils/api";
import { StaticDataTable, StaticDataColumnProps, StaticDataRow } from "../table/StaticDataTable";
import { SortDirection } from "../table/useTableData";
import { useCurrentOrganization, useOrganizationContext } from "src/contexts/OrganizationContext";
import TableBadge from "../table/TableBadge";
import { capitalizeStatus } from "src/utils/utils";
import TableDateTime, { getDateTimeSortValue } from "../table/TableDateTime";
import ActionsMenu from "../ActionsMenu";
import { ExplanationOfBenefit } from "src/shared/types";
import CoverageDetailsModal from "../modals/CoverageDetailsModal";

const statusColorMap: Record<string, string> = {
  active: "bg-emerald-500",
  cancelled: "bg-rose-500",
  draft: "bg-amber-500",
  entered_in_error: "bg-sky-500",
};

const CoverageColumns: StaticDataColumnProps[] = [
  { title: "Type", sortable: true },
  { title: "Created", sortable: true, defaultSort: true, defaultSortDirection: SortDirection.DESC },
  { title: "Insurer", sortable: true },
  { title: "Use", sortable: true },
  { title: "Total Amount", sortable: true },
  { title: "Status", sortable: true },
];

const eobToRows = (eob: ExplanationOfBenefit, onViewDetails: (eob: ExplanationOfBenefit) => void): StaticDataRow[] => {
  const typeDisplay = eob.payment?.[0]?.display ?? eob.netTotal?.[0]?.productName ?? "—";
  let total = 0;
  let currency = "";
  for (const item of eob.items) {
    if (!item.quantity || !item.value) continue;
    if (!currency && item.currency) currency = item.currency;
    total += (item.quantity * item.value);
  }

  return [
    { name: "Type", value: typeDisplay, element: <span className="text-gray-900">{typeDisplay}</span> },
    {
      name: "Created",
      value: getDateTimeSortValue(eob.created?.date, eob.created?.time),
      element: <TableDateTime date={eob.created?.date} startTime={eob.created?.time} />,
    },
    {
      name: "Insurer",
      value: eob.insurer?.display,
      element: <span className="text-gray-900">{eob.insurer?.display ?? "—"}</span>,
    },
    { name: "Use", value: eob.use, element: <span className="text-gray-900">{String(eob.use ?? "—")}</span> },
    {
      name: "Total Amount",
      value: total,
      element: <span className="text-gray-900 tabular-nums">{total !== 0 ? `${total} ${currency}`.trim() : "—"}</span>
    },
    {
      name: "Status",
      value: capitalizeStatus(eob.status),
      element: (
        <TableBadge
          text={capitalizeStatus(eob.status)}
          bgColorClass={statusColorMap[eob.status] ?? "bg-gray-500"}
        />
      ),
    },
    {
      name: "Actions",
      value: "",
      element: (
        <ActionsMenu
          actions={[
            {
              id: "view-details",
              label: "View advanced details",
              onClick: () => onViewDetails(eob),
            },
          ]}
        />
      ),
    },
  ];
};

type CoverageCardProps = {
  yearFrom: string;
  yearTo: string;
  refreshKey?: number;
};

const dummyCoverage: ExplanationOfBenefit[] = [
  {
    id: "1",
    status: "active",
    use: "inpatient",
    patientId: "1",
    billablePeriod: {
      start: new Date().toISOString().split('T')[0],
      end: new Date().toISOString().split('T')[0],
    },
    created: {
      date: new Date().toISOString().split('T')[0],
      time: "12:00:00",
    },
    insurer: {
      identifier: {
        use: "official",
        system: "http://hl7.org/fhir/v3/IdentifierType",
        value: "1",
      },
      display: "Insurer 1",
    },
    provider: {
      reference: "Provider/1",
      display: "Provider 1",
    },
    payee: {
      reference: "Payee/1",
      display: "Payee 1",
    },
    facility: "Facility 1",
    outcome: "Outcome 1",
    disposition: "Disposition 1",
    insurance: [
      {
        id: "1",
        status: "active",
        subscriberId: "1",
        beneficiaryReference: "Patient/1",
        period: {
          start: new Date().toISOString().split('T')[0],
          end: new Date().toISOString().split('T')[0],
        },
        payors: [
          {
            reference: "Coverage/1",
            display: "Coverage 1",
          },
        ],
        classes: [
          {
            value: "1",
            name: "Coverage 1",
          },
        ],
      },
    ],
    payment: [
      {
        amount: {
          value: 100,
          currency: "USD",
        },
        display: "Payment 1",
      },
    ],
    items: [
      {
        productName: "Item 1",
        value: 100,
        currency: "USD",
        quantity: 1,
        servicedDate: new Date().toISOString().split('T')[0],
        servicedTime: "12:00:00",
      },
    ],
    netTotal: [
      {
        productName: "Item 2",
        value: 200,
        currency: "USD",
      },
    ],
  },
];

const CoverageCard = ({ yearFrom, yearTo, refreshKey }: CoverageCardProps) => {
  const organization = useCurrentOrganization();
  const { patientId } = useOrganizationContext();
  const [ explanationOfBenefits, setExplanationOfBenefits ] = useState<ExplanationOfBenefit[]>([]);
  const [ loading, setLoading ] = useState(false);
  const [ selectedEob, setSelectedEob ] = useState<ExplanationOfBenefit | null>(null);

  const { isVerified } = useVerification();
  const showLoading = isVerified && loading;

  const fetchCoverage = useCallback(async () => {
    if (!organization || !patientId) {
      setExplanationOfBenefits([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const response = await apiGet(`/api/${organization.id}/patients/${patientId}/coverage`, { yearFrom, yearTo });
      const data = await response.json();

      if (data.error || !data.explanationOfBenefits) {
        setExplanationOfBenefits([]);
      } else {
        setExplanationOfBenefits(data.explanationOfBenefits);
      }
    } catch (_) {
      setExplanationOfBenefits([]);
    } finally {
      setLoading(false);
    }
  }, [ organization, patientId, yearFrom, yearTo ]);

  useEffect(() => {
    if (isVerified) {
      fetchCoverage();
    } else {
      setExplanationOfBenefits(dummyCoverage);
    }
  }, [ isVerified, fetchCoverage, refreshKey ]);

  const rows = useMemo(
    () => explanationOfBenefits.map((eob) => eobToRows(eob, setSelectedEob)),
    [ explanationOfBenefits ]
  );

  return (
    <BlurredCard>
      {showLoading ? (
        <div className="flex justify-center mt-8">
          <Loading message="Loading Coverage..." borderColor="border-brand-500" />
        </div>
      ) : (
        <div className="-mx-2">
          {rows.length > 0 ? (
            <>
              <StaticDataTable
                tableId="Coverage-table"
                columns={CoverageColumns}
                rows={rows}
                updateUrlParams={false}
                trailingColumnKey="Actions"
                rowId={() => "Coverage-row"}
              />
              {selectedEob && (
                <CoverageDetailsModal eob={selectedEob} onClose={() => setSelectedEob(null)} />
              )}
            </>
          ) : (
            <div className="text-center py-12 text-gray-400">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <CurrencyDollarIcon className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm font-medium">No Coverage records found</p>
            </div>
          )}
        </div>
      )}
    </BlurredCard>
  );
};

export default CoverageCard;
